import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, finalize, merge } from 'rxjs';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { ToastrService } from 'ngx-toastr';
import {
  EmailAuditDTO,
  EmailAuditService,
  EmailAuditStatus,
  ListEmailAuditFilterSpec,
  SUPPRESSED_EMAIL_TOOLTIP
} from '../../services/email-audit.service';
import { EmailAuditReadComponent } from './email-audit-read.component';
import { CustomerService } from '../../services/customer.service';
import { SaleService } from '../../services/sale.service';
import { SubscriptionService } from '../../services/subscription.service';
import type { ChargeDTO, CustomerDTO, SaleDTO, SubscriptionDTO } from '../../core/models';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { SaleReadComponent } from '../sales/sale-read.component';
import { SubscriptionReadComponent } from '../subscriptions/subscription-read.component';
import { SaleLinkReadComponent } from '../sale-links/sale-link-read.component';
import { SaleLinkDTO, SaleLinkService } from '../../services/sale-link.service';

@Component({
  selector: 'app-comunicados-list',
  templateUrl: './comunicados-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .comunicados-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .comunicados-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
        overflow: visible;
      }
      :host ::ng-deep .comunicados-primeng-table .p-datatable-tbody > tr > td:first-child {
        overflow: visible;
      }
      .email-status-badge {
        display: inline-grid;
        place-items: center;
        line-height: 0;
      }
      .email-status-badge i.pi {
        display: block;
        margin: 0;
        padding: 0;
        line-height: 1;
        width: 1em;
        height: 1em;
      }
      .email-status-badge i.pi::before {
        display: block;
        line-height: 1;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .comunicados-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .email-audit-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .email-audit-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    InputTextModule,
    SelectModule,
    DatePickerModule,
    DrawerModule,
    TooltipModule,
    EmailAuditReadComponent,
    CustomerReadComponent,
    SaleReadComponent,
    SubscriptionReadComponent,
    SaleLinkReadComponent
  ]
})
export class ComunicadosListComponent {
  private emailAuditService = inject(EmailAuditService);
  private customerService = inject(CustomerService);
  private saleService = inject(SaleService);
  private subscriptionService = inject(SubscriptionService);
  private saleLinkService = inject(SaleLinkService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  readonly skeletonRows = Array.from({ length: 8 }, (_, i) => i);

  emails = signal<EmailAuditDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  pageSizeOptions = [20, 50, 100];

  selectedEmail = signal<EmailAuditDTO | null>(null);
  detailsOpen = signal(false);
  detailsLoading = signal(false);
  hasActiveFilters = signal(false);

  isCustomerDetailsOpen = signal(false);
  customerDetailsLoading = signal(false);
  selectedCustomer = signal<CustomerDTO | null>(null);

  isSaleDetailsOpen = signal(false);
  saleDetailsLoading = signal(false);
  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);

  isSubscriptionDetailsOpen = signal(false);
  subscriptionDetailsLoading = signal(false);
  selectedSubscription = signal<SubscriptionDTO | null>(null);

  isSaleLinkDetailsOpen = signal(false);
  saleLinkDetailsLoading = signal(false);
  selectedSaleLink = signal<SaleLinkDTO | null>(null);

  statusOptions = [
    { label: 'Enviado', value: 'SENT' as EmailAuditStatus },
    { label: 'Fallido', value: 'FAILED' as EmailAuditStatus },
    { label: 'Pendiente', value: 'PENDING' as EmailAuditStatus },
    { label: 'Suprimido', value: 'SUPPRESSED' as EmailAuditStatus }
  ];

  filtersForm = this.fb.group({
    subject: [''],
    from: [''],
    to: [''],
    customer: [''],
    status: [null as EmailAuditStatus | null],
    createdAtRange: [null as Date[] | null]
  });

  constructor() {
    merge(
      this.filtersForm.controls.subject.valueChanges.pipe(debounceTime(400), distinctUntilChanged()),
      this.filtersForm.controls.from.valueChanges.pipe(debounceTime(400), distinctUntilChanged()),
      this.filtersForm.controls.to.valueChanges.pipe(debounceTime(400), distinctUntilChanged()),
      this.filtersForm.controls.customer.valueChanges.pipe(debounceTime(400), distinctUntilChanged()),
      this.filtersForm.controls.status.valueChanges,
      this.filtersForm.controls.createdAtRange.valueChanges
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.syncActiveFilters();
        if (this.initialLoadDone()) {
          this.currentPage.set(0);
          this.loadEmails();
        }
      });
  }

  ngOnInit(): void {
    this.loadEmails();
  }

  buildSpecFromFilters(): ListEmailAuditFilterSpec {
    const v = this.filtersForm.getRawValue();
    const spec: ListEmailAuditFilterSpec = {};
    const subject = v.subject?.trim();
    const from = v.from?.trim();
    const to = v.to?.trim();
    const customer = v.customer?.trim();
    if (subject) spec['subject[lk]'] = subject;
    if (from) spec['from[lk]'] = from;
    if (to) spec['to[lk]'] = to;
    if (customer) spec['customer[lk]'] = customer;
    if (v.status) spec.status = v.status;
    const range = v.createdAtRange;
    if (range?.[0]) spec['createdAt[gte]'] = this.toIsoStart(range[0]);
    if (range?.[1]) spec['createdAt[lte]'] = this.toIsoEnd(range[1]);
    return spec;
  }

  loadEmails(): void {
    this.loading.set(true);
    this.error.set(null);
    this.emailAuditService
      .list(this.buildSpecFromFilters(), {
        page: this.currentPage(),
        size: this.pageSize(),
        sort: ['createdAt,desc']
      })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.ok) {
            this.emails.set(response.body ?? []);
            if (response.page) {
              this.totalItems.set(response.page.totalItems ?? 0);
              this.pageSize.set(response.page.pageSize ?? this.pageSize());
              this.currentPage.set(Math.max(0, (response.page.currentPage ?? 1) - 1));
            } else {
              this.totalItems.set(response.body?.length ?? 0);
            }
            this.error.set(null);
          } else {
            this.emails.set([]);
            this.totalItems.set(0);
            this.error.set(response.message || 'No se pudo cargar el historial de comunicados.');
          }
          this.initialLoadDone.set(true);
        },
        error: () => {
          this.error.set('No se pudo cargar el historial de comunicados.');
          this.toastr.error('Error al cargar comunicados');
        }
      });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.pageSize();
    const first = event.first ?? 0;
    this.pageSize.set(rows);
    this.currentPage.set(Math.floor(first / rows));
    this.loadEmails();
  }

  openDetails(email: EmailAuditDTO): void {
    if (!email.id) return;
    this.detailsOpen.set(true);
    this.detailsLoading.set(true);
    this.selectedEmail.set(email);
    this.emailAuditService
      .getById(email.id)
      .pipe(finalize(() => this.detailsLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.ok && response.body) {
            this.selectedEmail.set(response.body);
          }
        },
        error: () => this.toastr.error('No se pudo cargar el detalle del correo')
      });
  }

  clearFilters(): void {
    this.filtersForm.reset({
      subject: '',
      from: '',
      to: '',
      customer: '',
      status: null,
      createdAtRange: null
    });
    this.syncActiveFilters();
    this.currentPage.set(0);
    this.loadEmails();
  }

  private syncActiveFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(
      !!(
        raw.subject?.trim() ||
        raw.from?.trim() ||
        raw.to?.trim() ||
        raw.customer?.trim() ||
        raw.status ||
        raw.createdAtRange?.[0] ||
        raw.createdAtRange?.[1]
      )
    );
  }

  statusLabel(status?: EmailAuditStatus): string {
    switch (status) {
      case 'SENT':
        return 'Enviado';
      case 'FAILED':
        return 'Fallido';
      case 'PENDING':
        return 'Pendiente';
      case 'SUPPRESSED':
        return 'Suprimido';
      default:
        return status ?? '—';
    }
  }

  emailStatusTooltip(email: EmailAuditDTO): string {
    if (email.status === 'SUPPRESSED') {
      return SUPPRESSED_EMAIL_TOOLTIP;
    }
    if (email.status === 'SENT' && email.openedAt) {
      const count = email.openCount ?? 1;
      const countLabel = count === 1 ? '1 apertura' : `${count} aperturas`;
      return `Abierto el ${this.formatDateTime(email.openedAt)} · ${countLabel}`;
    }
    return this.statusLabel(email.status);
  }

  private formatDateTime(value: string): string {
    return new Date(value).toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  customerDisplayName(email: EmailAuditDTO): string {
    return email.customerName?.trim() || email.customerEmail?.trim() || '';
  }

  openCustomerDetails(customerId: number): void {
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: () => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
      }
    });
  }

  openSaleDetails(saleId: number): void {
    if (!saleId) return;
    this.isSaleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          if (detail.id) {
            this.saleService.getSaleCharges(detail.id).subscribe({
              next: (chargesResp) => this.saleCharges.set(chargesResp.body ?? []),
              error: () => this.saleCharges.set([])
            });
          }
        } else {
          this.toastr.error(response.message || 'No se pudo cargar la venta');
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: () => {
        this.saleDetailsLoading.set(false);
        this.toastr.error('Error al cargar la venta');
        this.closeSaleDetails();
      }
    });
  }

  openSubscriptionDetails(subscriptionId: number): void {
    if (!subscriptionId) return;
    this.isSubscriptionDetailsOpen.set(true);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(true);
    this.subscriptionService.getSubscriptionById(subscriptionId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedSubscription.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar la suscripción');
          this.closeSubscriptionDetails();
        }
        this.subscriptionDetailsLoading.set(false);
      },
      error: () => {
        this.subscriptionDetailsLoading.set(false);
        this.toastr.error('Error al cargar la suscripción');
        this.closeSubscriptionDetails();
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  closeSaleDetails(): void {
    this.isSaleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  openSaleLinkDetails(saleLinkId: number): void {
    if (!saleLinkId) return;
    this.isSaleLinkDetailsOpen.set(true);
    this.selectedSaleLink.set(null);
    this.saleLinkDetailsLoading.set(true);
    this.saleLinkService.getById(saleLinkId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedSaleLink.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar la liga de pago');
          this.closeSaleLinkDetails();
        }
        this.saleLinkDetailsLoading.set(false);
      },
      error: () => {
        this.saleLinkDetailsLoading.set(false);
        this.toastr.error('Error al cargar la liga de pago');
        this.closeSaleLinkDetails();
      }
    });
  }

  closeSubscriptionDetails(): void {
    this.isSubscriptionDetailsOpen.set(false);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(false);
  }

  closeSaleLinkDetails(): void {
    this.isSaleLinkDetailsOpen.set(false);
    this.selectedSaleLink.set(null);
    this.saleLinkDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDetails();
  }

  onSubscriptionDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSubscriptionDetails();
  }

  onSaleLinkDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleLinkDetails();
  }

  editCustomerFromModal(): void {
    const customer = this.selectedCustomer();
    if (!customer?.id) return;
    this.closeCustomerDetails();
    this.router.navigate(['/customers', customer.id]);
  }

  editSaleFromModal(): void {
    const sale = this.selectedSale();
    if (!sale?.id) return;
    this.closeSaleDetails();
    this.router.navigate(['/sales', sale.id]);
  }

  editSubscriptionFromModal(): void {
    const subscription = this.selectedSubscription();
    if (!subscription?.id) return;
    this.closeSubscriptionDetails();
    this.router.navigate(['/subscriptions', subscription.id]);
  }

  editSaleLinkFromModal(): void {
    const saleLink = this.selectedSaleLink();
    if (!saleLink?.id) return;
    this.closeSaleLinkDetails();
    this.router.navigate(['/sale-links', saleLink.id]);
  }

  private toIsoStart(date: Date): string {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }

  private toIsoEnd(date: Date): string {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }
}
